import React, { useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { useBackToClose } from "../../utils/backToClose";

const typeOptions = [
  "Internal",
  "Client Delivery",
  "Automation",
  "Compliance",
  "Marketing",
  "Data Engineering",
  "Content",
  "Analytics",
  "Growth",
  "AI",
];

const statusOptions = ["Active", "Closed"];
const priorityOptions = ["Low", "Medium", "High", "Critical"];

const requiredFieldMessages = {
  projectName: "Project Name is required.",
  projectType: "Project Type is required.",
  status: "Status is required.",
  priority: "Priority is required.",
  targets: "Targets is required.",
  startDate: "Start Date is required.",
  dueDate: "Due Date is required.",
};

const emptyForm = {
  projectName: "",
  projectType: "",
  status: "",
  priority: "",
  targets: "",
  startDate: "",
  dueDate: "",
  assignees: [],
  notes: "",
};

const isUrl = (value) => /^https?:\/\/\S+$/i.test(value);

const renderLineWithLinks = (line, lineIndex, totalLines) => {
  const parts = line.split(/(https?:\/\/\S+)/g);

  return (
    <React.Fragment key={`line-${lineIndex}`}>
      {parts.map((part, partIndex) => {
        if (isUrl(part)) {
          return (
            <a
              key={`part-${lineIndex}-${partIndex}`}
              href={part}
              target="_blank"
              rel="noreferrer"
              className="text-[#2470B7] underline break-all dark:text-blue-400"
            >
              {part}
            </a>
          );
        }

        return <span key={`part-${lineIndex}-${partIndex}`}>{part}</span>;
      })}
      {lineIndex < totalLines - 1 && <br />}
    </React.Fragment>
  );
};

const mapProjectToForm = (project) => {
  if (!project) return emptyForm;

  return {
    projectName: project.projectName || "",
    projectType: project.projectType || "",
    status: project.status || "",
    priority: project.priority || "",
    targets: project.targets || "",
    startDate: project.startDate || "",
    dueDate: project.dueDate || "",
    assignees: Array.isArray(project.assignedUsers) ? project.assignedUsers : [],
    notes: project.notes || "",
  };
};

function AddProjectModal({
  show,
  onClose,
  onCreateProject,
  users = [],
  projectTypes = typeOptions,
  mode = "add",
  initialProject = null,
}) {
  useBackToClose(show, onClose);
  const [form, setForm] = useState(() => mapProjectToForm(initialProject));
  const [errors, setErrors] = useState({});

  const selectedAssignees = useMemo(
    () => users.filter((user) => form.assignees.includes(user)),
    [users, form.assignees]
  );

  if (!show) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const toggleAssignee = (user) => {
    setForm((prev) => {
      const exists = prev.assignees.includes(user);
      const assignees = exists
        ? prev.assignees.filter((item) => item !== user)
        : [...prev.assignees, user];

      return { ...prev, assignees };
    });
  };

  const validate = () => {
    const validationErrors = {};

    Object.entries(requiredFieldMessages).forEach(([field, message]) => {
      if (!String(form[field] || "").trim()) {
        validationErrors[field] = message;
      }
    });

    if (form.status && !statusOptions.includes(form.status)) {
      validationErrors.status = "Status must be one of: Planned, In Progress, On Hold, Completed, Canceled.";
    }

    if (form.startDate && form.dueDate && form.dueDate < form.startDate) {
      validationErrors.dueDate = "Due Date cannot be earlier than Start Date.";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

    onCreateProject({
      projectName: form.projectName.trim(),
      projectType: form.projectType,
      status: form.status,
      priority: form.priority,
      targets: Number(form.targets ?? 0),
      startDate: form.startDate,
      dueDate: form.dueDate,
      assignedUsers: form.assignees,
      notes: form.notes,
    });
  };

  const isEditMode = mode === "edit";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-3 sm:p-6">
      <button
        type="button"
        aria-label={isEditMode ? "Close edit project modal" : "Close add project modal"}
        onClick={onClose}
        className="absolute inset-0"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-project-modal-title"
        className="relative w-full max-w-5xl rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50"
      >
        <div className="flex items-start justify-between border-b border-app-primary px-5 py-4 sm:px-6">
          <div>
            <h3 id="add-project-modal-title" className="text-xl font-semibold text-app-heading">
              {isEditMode ? "Edit Project" : "Add New Project"}
            </h3>
            <p className="text-sm text-app-muted">
              {isEditMode
                ? "Update and save project configuration."
                : "Create and configure a new project."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[85vh] overflow-y-auto px-5 py-5 sm:px-6">
          <div className="space-y-6">
            <section>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-muted">
                Project Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label htmlFor="projectName" className="mb-1 block text-sm font-medium text-app-secondary">
                    Project Name
                  </label>
                  <input
                    id="projectName"
                    type="text"
                    value={form.projectName}
                    onChange={(event) => updateField("projectName", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Enter project name"
                  />
                  {errors.projectName && <p className="mt-1 text-xs text-app-error">{errors.projectName}</p>}
                </div>

                <div>
                  <label htmlFor="projectType" className="mb-1 block text-sm font-medium text-app-secondary">
                    Project Type
                  </label>
                  <select
                    id="projectType"
                    value={form.projectType}
                    onChange={(event) => updateField("projectType", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  >
                    <option value="">Select type</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.projectType && <p className="mt-1 text-xs text-app-error">{errors.projectType}</p>}
                </div>

                <div>
                  <label htmlFor="status" className="mb-1 block text-sm font-medium text-app-secondary">
                    Status
                  </label>
                  <select
                    id="status"
                    value={form.status}
                    onChange={(event) => updateField("status", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  >
                    <option value="">Select status</option>
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                  {errors.status && <p className="mt-1 text-xs text-app-error">{errors.status}</p>}
                </div>

                <div>
                  <label htmlFor="priority" className="mb-1 block text-sm font-medium text-app-secondary">
                    Priority
                  </label>
                  <select
                    id="priority"
                    value={form.priority}
                    onChange={(event) => updateField("priority", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  >
                    <option value="">Select priority</option>
                    {priorityOptions.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                  {errors.priority && <p className="mt-1 text-xs text-app-error">{errors.priority}</p>}
                </div>

                <div>
                  <label htmlFor="targets" className="mb-1 block text-sm font-medium text-app-secondary">
                    Targets
                  </label>
                  <input
                    id="targets"
                    type="number"
                    min="0"
                    step="1"
                    value={form.targets}
                    onChange={(event) => updateField("targets", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Enter target count"
                  />
                  {errors.targets && <p className="mt-1 text-xs text-app-error">{errors.targets}</p>}
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-muted">
                Project Timeline
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="startDate" className="mb-1 block text-sm font-medium text-app-secondary">
                    Start Date
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    value={form.startDate}
                    onChange={(event) => updateField("startDate", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  />
                  {errors.startDate && <p className="mt-1 text-xs text-app-error">{errors.startDate}</p>}
                </div>

                <div>
                  <label htmlFor="dueDate" className="mb-1 block text-sm font-medium text-app-secondary">
                    Due Date
                  </label>
                  <input
                    id="dueDate"
                    type="date"
                    value={form.dueDate}
                    onChange={(event) => updateField("dueDate", event.target.value)}
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  />
                  {errors.dueDate && <p className="mt-1 text-xs text-app-error">{errors.dueDate}</p>}
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-muted">
                Team Assignment
              </h4>

              {selectedAssignees.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedAssignees.map((user) => (
                    <span
                      key={`chip-${user}`}
                      className="inline-flex items-center rounded-full border border-app-info bg-app-info px-3 py-1 text-xs font-semibold text-app-info"
                    >
                      {user}
                    </span>
                  ))}
                </div>
              )}

              <div className="max-h-[180px] overflow-y-auto rounded-lg border border-app-primary p-3">
                <div className="flex flex-wrap gap-2">
                  {users.map((user) => {
                    const selected = form.assignees.includes(user);

                    return (
                      <button
                        key={user}
                        type="button"
                        onClick={() => toggleAssignee(user)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                          selected
                            ? "border-[#2470B7] bg-blue-50 text-[#2470B7] dark:border-blue-400 dark:bg-blue-500/20 dark:text-blue-300"
                            : "border-app-primary bg-app-input text-app-secondary hover:bg-app-hover hover:text-app-primary"
                        }`}
                      >
                        {user}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-muted">Notes</h4>
              <textarea
                rows={8}
                value={form.notes}
                onChange={(event) => updateField("notes", event.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm leading-6 text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                placeholder="Add team instructions, links, meeting notes, and references..."
              />

              {form.notes.trim() && (
                <div className="mt-3 rounded-lg border border-app-primary bg-app-secondary p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-app-muted">
                    Notes Preview
                  </p>
                  <div className="max-h-56 overflow-y-auto whitespace-pre-wrap break-words text-sm text-app-secondary">
                    {form.notes
                      .split("\n")
                      .map((line, index, allLines) => renderLineWithLinks(line, index, allLines.length))}
                  </div>
                </div>
              )}
            </section>
          </div>

          <div className="sticky bottom-0 mt-6 flex justify-end gap-3 border-t border-app-primary bg-app-card py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              {isEditMode ? "Save Changes" : "Create Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddProjectModal;
